<script lang="ts" setup>
import { ref, useSlots, computed, nextTick, watchEffect, watch, onBeforeUnmount } from 'vue';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { unrefElement, refAutoReset, useVModel } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { IconClose } from '@/icons';
import { useStack, useZIndex, useTrueSelfClick, useFocusScope } from '../../composables';
import { ObModalOverlay } from '../modal-overlay';
import { UiHeader } from '../ui-header';
import { hasSlotContent } from '../../utils';
import { MODALS_STACK_NAME } from './shared';
import { type ModalDeactivationTrigger } from './interfaces';
type ModalSize = 'small' | 'medium' | 'large';

interface Props {
  active?: boolean;
  closeButton?: boolean;
  deactivateOnEscapePress?: boolean;
  deactivateOnOverlayClick?: boolean;
  onBeforeDeactivate?: () => Promise<void | boolean> | void | boolean;
  title?: string;
  size?: ModalSize;
}
defineOptions({
  inheritAttrs: false,
});
const props = withDefaults(defineProps<Props>(), {
  active: false,
  closeButton: true,
  deactivateOnEscapePress: true,
  deactivateOnOverlayClick: true,
  onBeforeDeactivate: undefined,
  title: undefined,
  size: undefined,
});
const emit = defineEmits<{
  'update:active': [active: boolean];
  activated: [];
  deactivated: [trigger?: ModalDeactivationTrigger];
}>();
const active = useVModel(props, 'active', emit, {
  passive: true,
});
const portalActive = ref(false);
watchEffect(() => {
  if (active.value) {
    portalActive.value = true;
  }
});
const rootRef = ref();
const { zIndex } = useZIndex({ active: portalActive, size: 2 });
const { activate: activateTrap, deactivate: deactivateTrap } = useFocusTrap(rootRef, {
  escapeDeactivates: false,
  allowOutsideClick: true,
  clickOutsideDeactivates: false,
  returnFocusOnDeactivate: true,
  fallbackFocus: () => unrefElement(rootRef) ?? 'body',
});
const { last } = useStack(MODALS_STACK_NAME, {
  active: portalActive,
});
const shaking = refAutoReset(false, 1000);
function activate() {
  active.value = true;
  emit('activated');
}
function deactivate(trigger?: ModalDeactivationTrigger) {
  active.value = false;
  emit('deactivated', trigger);
}
async function tryDeactivate(trigger?: ModalDeactivationTrigger) {
  if (typeof props.onBeforeDeactivate === 'function') {
    const allowed = await props.onBeforeDeactivate();
    if (allowed === false) {
      shaking.value = true;
      return;
    }
  }
  deactivate(trigger);
}
function onEscapePress() {
  if (!props.deactivateOnEscapePress) {
    return;
  }
  tryDeactivate('escape');
}
useTrueSelfClick(rootRef, () => {
  if (!props.deactivateOnOverlayClick) {
    return;
  }
  tryDeactivate('overlay-click');
});
let bodyScrollLocked = false;
watch(
  active,
  (value, oldValue) => {
    if (value) {
      nextTick(() => {
        if (!active.value) {
          return;
        }
        activateTrap();
        const el = unrefElement(rootRef);
        if (el) {
          disableBodyScroll(el, {
            reserveScrollBarGap: true,
          });
          bodyScrollLocked = true;
        }
      });
      return;
    }
    if (typeof oldValue === 'undefined') {
      return;
    }
    deactivateTrap();
    const el = unrefElement(rootRef);
    if (el && bodyScrollLocked) {
      enableBodyScroll(el);
      bodyScrollLocked = false;
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  deactivateTrap();
  const el = unrefElement(rootRef);
  if (el && bodyScrollLocked) {
    enableBodyScroll(el);
    bodyScrollLocked = false;
  }
});
useFocusScope(rootRef);
const $slots = useSlots();
const hasTitle = computed(() => props.title || hasSlotContent($slots.title));
const hasHeader = computed(() => props.closeButton || hasTitle.value);
const sharedSlotScope = { active, activate, deactivate };
</script>

<template>
  <slot name="activator" v-bind="sharedSlotScope" />
  <Teleport v-if="portalActive" to="body">
    <Transition
      appear
      :enter-from-class="$style.transitionEnterFrom"
      :enter-active-class="$style.transitionEnterActive"
      :leave-active-class="$style.transitionLeaveActive"
      :leave-to-class="$style.transitionLeaveTo"
      @after-leave="portalActive = false"
    >
      <div
        v-if="active"
        ref="rootRef"
        :class="[$style.root, { [$style.shakeAnimation]: shaking }]"
        role="dialog"
        aria-modal="true"
        :style="{ zIndex: zIndex && zIndex + 1 }"
        tabindex="-1"
        @keydown.esc.stop.capture="onEscapePress"
      >
        <div :class="[$style.box, props.size && $style[`size-${props.size}`]]" v-bind="$attrs">
          <header v-if="hasHeader" :class="$style.header">
            <UiHeader v-if="hasTitle" level="3" tag="div" :class="$style.title">
              <slot name="title">{{ props.title }}</slot>
            </UiHeader>
            <button
              v-if="props.closeButton"
              type="button"
              :class="$style.close"
              aria-label="Close"
              @click="tryDeactivate('close-button')"
            >
              <IconClose aria-hidden="true" />
            </button>
          </header>
          <div :class="$style.body">
            <slot v-bind="sharedSlotScope" />
          </div>
        </div>
      </div>
    </Transition>
    <ObModalOverlay v-if="last" :z-index="zIndex" />
  </Teleport>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/colors';
@use '../../styles/typography';

.root {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: auto;
  will-change: opacity, transform;
  outline: none;
  font-family: typography.$font-family-primary;
  scrollbar-gutter: stable;
  display: grid;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  grid-template-columns: minmax(0, 1fr);
}

.box {
  position: relative;
  z-index: 200;
  box-sizing: border-box;
  background: colors.$white;
  color: var(--ui2-content-primary, #010000);
  padding: 24px;
  margin: auto;
  border-radius: shared.$border-radius-m;
  min-width: 480px;
  min-height: 260px;
  max-width: 100%;
  box-shadow: 0px 0px 18px rgba(2, 17, 72, 0.2);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
}

.size-small {
  max-width: 480px;
}

.size-medium {
  max-width: 640px;
}

.size-large {
  max-width: 960px;
}

.header {
  margin-bottom: 24px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

.body {
  grid-row: 2;
}

.title {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  min-width: 0;
}

.close {
  @include shared.reset-button();
  flex: none;
  margin-left: 8px;
  display: flex;

  &:focus-visible {
    outline: 1px solid colors.$hyperlink;
    outline-offset: -1px;
  }
}

@keyframes shake {
  from,
  to {
    transform: translate3d(0, 0, 0);
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translate3d(5px, 0, 0);
  }

  20%,
  40%,
  60%,
  80% {
    transform: translate3d(0, 0, 0);
  }
}

.shakeAnimation {
  animation: shake 1s linear 0.2s infinite;
}

.transitionEnterFrom,
.transitionLeaveTo {
  opacity: 0.5;
  transform: scale(0.9);
}

.transitionEnterActive,
.transitionLeaveActive {
  transition-property: opacity, transform;
  overflow: hidden;
}

.transitionEnterActive {
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
}

.transitionLeaveActive {
  transition-duration: 0.15s;
  transition-timing-function: ease-in;
}
</style>
